import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useEffect, useState } from 'react';
import type { KnowledgeGroup } from './knowledge-groups';


export type KnowledgeState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; groups: KnowledgeGroup[] };

type Store = { state: KnowledgeState; listeners: Set<() => void>; inflight: Promise<void> | null };
const store: Store = { state: { kind: 'loading' }, listeners: new Set(), inflight: null };

function setStoreState(next: KnowledgeState): void {
  store.state = next;
  store.listeners.forEach((l) => l());
}

async function fetchKnowledge(): Promise<void> {
  try {
    const res = await apiFetch(`${API_BASE}/api/knowledge`, { credentials: 'include' });
    if (!res.ok) {
      if (store.state.kind !== 'ready') {
        setStoreState({ kind: 'error' });
      }
      return;
    }
    const data = (await res.json()) as {
      groups?: KnowledgeGroup[];
      sections?: { title: string; content: string }[];
    };
    // Pre-groups API fallback: local dev proxies /api to prod, which keeps
    // returning the old flat `sections` shape until this change deploys.
    // Render those as one unlabeled bucket instead of an empty screen.
    const groups =
      data.groups ??
      (data.sections?.length
        ? [{ id: 'other' as const, entries: data.sections.map((s) => ({ path: s.title, title: s.title, content: s.content })) }]
        : []);
    setStoreState({ kind: 'ready', groups });
  } catch {
    if (store.state.kind !== 'ready') {
      setStoreState({ kind: 'error' });
    }
  }
}

function ensureFetched(): Promise<void> {
  if (store.inflight) return store.inflight;
  store.inflight = (async () => {
    await fetchKnowledge();
    store.inflight = null;
  })();
  return store.inflight;
}

/** `enabled: false` holds the fetch for a caller that may have nothing to look up; the state it reads is the
 *  shared store's, whatever an enabled reader already loaded. */
export function useKnowledge(options?: { enabled?: boolean }): { state: KnowledgeState; refresh: () => Promise<void> } {
  const enabled = options?.enabled ?? true;
  const [, force] = useState({});
  useEffect(() => {
    const listener = () => force({});
    store.listeners.add(listener);
    if (enabled) void ensureFetched();
    return () => {
      store.listeners.delete(listener);
    };
  }, [enabled]);
  return { state: store.state, refresh: fetchKnowledge };
}
